import React, { useEffect, useRef, useState } from "react";

import { Grid, IconButton, useTheme } from "@mui/material";
import { Skeleton } from "@mui/material";
import { PRIVATE_ROUTES } from "Constants";
import { Sms } from "iconsax-react";
import { useHistory } from "react-router-dom";
import { ResourceName, ResourceSubGroup } from "Util/enums";
import FeatureLocked from "Components/FeatureLocked";
import {
  checkResourceCan,
  FN_NAMES,
  hasPermission,
  isShowAction,
  managementPermissions,
  operationsPermissions,
  warehousingPermissions,
} from "Util/permissions";

import { useAppState } from "../../../../context/StateContext";
import useActionSyncUser from "../../../../hooks/useSyncUser";
import { Checklist } from "../../../../models/checklist";
import { Company } from "../../../../models/company";
import { Report as IReport } from "../../../../models/report";
import { ReportGroup } from "../../../../models/reports.groups";
import groupBy from "../../../../util/groupBy";
import FavoriteReports from "./FavoriteReports";
import { useActionReport } from "./hooks/useAction";
import ReportItem from "./ReportItem";
import SearchFilter from "./SearchFilter";
import useStyles from "./styles";
import TitlePage from "./TitlePage";
import { GenerateReportDrawer, GenerateReportDrawerHandles } from "Components/GenerateReport";
import { ResourceCanEnum, ResourceObjectTypeEnum } from "Util/enums/accessRule";
import { isMoving, isUserTypeAdmin } from "Util/validation";
import { useQuickOrderFeature } from "Hooks/useQuickOrderFeature";
import { applyOrderReportFlagToGroups } from "./quickOrderReportVisibility";

type ReportsOnly =
  | "favorites"
  | "management"
  | "finance"
  | "business"
  | "operations"
  | "warehousing";

interface Groups {
  reportGroupName: string;
  reportGroupItems: ReportGroup[];
}

type ReportProps = {
  only?: ReportsOnly;
};

const Report: React.FC<ReportProps> = ({ only }) => {
  const { currentUser, userPermissions } = useAppState();
  const { managementOrderReportEnabled } = useQuickOrderFeature();
  const { getAllReportGroup, getAllCheckList, useUserUpdateStatus } = useActionReport();
  const theme = useTheme();
  const history = useHistory();

  const [groups, setGroups] = useState<Groups[]>([]);
  const [reportsData, setReportsData] = useState<ReportGroup[]>([]);
  const [checklists, setChecklists] = useState<Checklist[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [favoriteReports, setFavoriteReports] = useState<IReport[]>(
    currentUser?.favoritesReports || []
  );
  const [favoriteChecklists, setFavoriteChecklists] = useState<Checklist[]>(
    currentUser?.favoritesChecklists || []
  );

  const [favoriteTimeout, setFavoriteTimeout] = useState<any>(0);

  const generateReportRef = useRef<GenerateReportDrawerHandles>(null);

  const classes = useStyles();

  const normalize = (s?: string) => (s || "").toLowerCase();
  const matchesOnlyGroup = (groupName?: string, onlyVal?: ReportsOnly) => {
    if (!onlyVal || onlyVal === "favorites") return true;
    return normalize(groupName) === normalize(onlyVal);
  };

  const groupReportsData = (groupData: ReportGroup[], onlyVal?: ReportsOnly) => {
    const filteredByOnly =
      onlyVal && onlyVal !== "favorites"
        ? groupData.filter((g) => matchesOnlyGroup(g.group, onlyVal))
        : groupData;

    const reportsGroupData: { [x: string]: Array<ReportGroup> } = groupBy(filteredByOnly, "group");
    const groupedReports: [string, Array<ReportGroup>][] = Object.entries(reportsGroupData);

    setGroups(
      groupedReports.map((group) => {
        return { reportGroupName: String(group[0]), reportGroupItems: group[1] };
      })
    );
  };

  const { mutate: syncUser } = useActionSyncUser();
  const { data, isFetching: loadingReportGroup } = getAllReportGroup();
  const { data: checklistData, isFetching: loadingChecklists } = getAllCheckList();
  const { mutate: updateUser } = useUserUpdateStatus(syncUser);
  const [reportsToView, setReportsToView] = useState<any[]>([]);
  const [reportPermission, setReportPermission] = useState<{
    view: boolean;
    favorite: boolean;
    emailSubscription: boolean;
    business: boolean;
    operations: boolean;
    orders: boolean;
  }>({
    view: false,
    favorite: false,
    emailSubscription: false,
    business: false,
    operations: false,
    orders: false,
  });

  useEffect(() => {
    if (userPermissions && userPermissions?.length > 0) {
      setReportPermission({
        view: hasPermission(userPermissions, FN_NAMES.FUNCTION_REPORTS_GENERAL_VIEW),
        favorite: isMoving(currentUser?.company)
          ? hasPermission(userPermissions, FN_NAMES.FUNCTION_REPORTS_GENERAL_FAVORITES)
          : hasPermission(userPermissions, FN_NAMES.FUNCTION_REPORTS_FAVORITES_VIEW),
        emailSubscription: hasPermission(
          userPermissions,
          FN_NAMES.FUNCTION_REPORTS_GENERAL_EMAIL_SUBSCRIPTIONS
        ),
        business: checkResourceCan(
          userPermissions,
          ResourceName.BUSINESS,
          ResourceCanEnum.VIEW,
          ResourceName.ANALYTICS
        ),
        operations: checkResourceCan(
          userPermissions,
          ResourceName.OPERATIONS,
          ResourceCanEnum.VIEW,
          ResourceName.ANALYTICS
        ),
        orders: checkResourceCan(
          userPermissions,
          ResourceName.ORDERS,
          ResourceCanEnum.VIEW,
          ResourceName.ANALYTICS
        ),
      });
      const reports = userPermissions
        ?.map((permission) => {
          if (
            permission?.objectType === ResourceObjectTypeEnum.FUNCTION &&
            permission?.objectDetails?.objectData?.includes("reports")
          ) {
            return permission;
          }
          return undefined;
        })
        ?.filter((permission) => !!permission);
      setReportsToView(reports);
    }
  }, [userPermissions, currentUser?.company]);

  useEffect(() => {
    if (data) {
      const reportGroups = applyOrderReportFlagToGroups(data, managementOrderReportEnabled);
      setReportsData(reportGroups);
      groupReportsData(reportGroups, only);
    }
  }, [data, only, managementOrderReportEnabled]);

  useEffect(() => {
    if (checklistData?.data) {
      setChecklists(checklistData?.data);
    }
  }, [checklistData]);

  const goToEditSubscriptionPage = () => {
    history.replace(PRIVATE_ROUTES.MANAGE_EMAIL_SUBSCRIPTIONS);
  };

  const addToFavorites = (reportId?: number, checklistId?: number) => {
    if (reportId) {
      addReportToFavorites(reportId);
    } else if (checklistId) addChecklistToFavorites(checklistId);
  };

  const removeFromFavorites = (reportId?: number, checklistId?: number) => {
    if (reportId) {
      removeReportFromFavorites(reportId);
    } else if (checklistId) removeChecklistFromFavorites(checklistId);
  };

  const handleUpdateUserFavorites = (reports: IReport[], checklists: Checklist[]) => {
    if (currentUser && currentUser?.id) {
      updateUser({
        id: currentUser?.id,
        user: {
          ...currentUser,
          favoritesReports: reports,
          favoritesChecklists: checklists,
        },
      });
    }
  };

  const addReportToFavorites = (reportId: number) => {
    if (favoriteTimeout) clearTimeout(favoriteTimeout);

    let reportsToUpdate = favoriteReports;

    if (reportsData && reportsData?.length) {
      reportsData?.forEach((reportGroup) => {
        if (reportGroup?.reports && reportGroup?.reports?.length) {
          const report = reportGroup?.reports?.find((report) => report?.id === reportId);

          if (report) {
            const reportWithGroup = report.reportGroup ? report : { ...report, reportGroup };
            reportsToUpdate = favoriteReports
              ? [...favoriteReports, reportWithGroup]
              : [reportWithGroup];
            setFavoriteReports(reportsToUpdate);
          }
        }
      });
    }

    if (currentUser && currentUser?.id && reportsToUpdate) {
      setFavoriteTimeout(
        setTimeout(
          () => handleUpdateUserFavorites(reportsToUpdate || [], favoriteChecklists || []),
          1000
        )
      );
    }
  };

  const addChecklistToFavorites = (checklistId: number) => {
    if (favoriteTimeout) clearTimeout(favoriteTimeout);

    let checklistsToUpdate = favoriteChecklists;

    const checklistFound =
      checklists && checklists?.length
        ? checklists?.find((checklist) => checklist?.id === checklistId)
        : null;

    if (checklistFound) {
      checklistsToUpdate = favoriteChecklists
        ? [...favoriteChecklists, checklistFound]
        : [checklistFound];
      setFavoriteChecklists(checklistsToUpdate);
    }

    if (currentUser && currentUser?.id && checklistsToUpdate) {
      setFavoriteTimeout(
        setTimeout(
          () => handleUpdateUserFavorites(favoriteReports || [], checklistsToUpdate || []),
          1000
        )
      );
    }
  };

  const removeReportFromFavorites = (reportId: number) => {
    if (favoriteTimeout) clearTimeout(favoriteTimeout);

    if (currentUser && currentUser?.id) {
      const updatedFavorites = favoriteReports?.filter(
        (favoriteReport) => favoriteReport.id !== reportId
      );

      setFavoriteReports(updatedFavorites || []);

      if (updatedFavorites) {
        setFavoriteTimeout(
          setTimeout(
            () => handleUpdateUserFavorites(updatedFavorites || [], favoriteChecklists || []),
            1000
          )
        );
      }
    }
  };

  const removeChecklistFromFavorites = (checklistId: number) => {
    if (favoriteTimeout) clearTimeout(favoriteTimeout);

    if (currentUser && currentUser?.id) {
      const updatedFavorites = favoriteChecklists?.filter(
        (favoriteChecklist) => favoriteChecklist.id !== checklistId
      );

      setFavoriteChecklists(updatedFavorites || []);

      if (updatedFavorites) {
        setFavoriteTimeout(
          setTimeout(
            () => handleUpdateUserFavorites(favoriteReports || [], updatedFavorites || []),
            1000
          )
        );
      }
    }
  };

  const handleSearch = (searchValue: string) => {
    setSearchValue(searchValue);

    let base: ReportGroup[] =
      only && only !== "favorites"
        ? reportsData.filter((g) => matchesOnlyGroup(g.group, only))
        : reportsData;

    let reportsGroup: ReportGroup[] = [];

    if (searchValue) {
      base.forEach((reportData) => {
        if (reportData.reports && reportData.reports.length > 0) {
          const searchResults = reportData.reports.filter((report) =>
            report?.name?.toLowerCase()?.includes(searchValue.toLowerCase())
          );

          if (searchResults.length > 0) {
            reportsGroup.push({ ...reportData, reports: searchResults });
          }
        }
      });
    } else {
      reportsGroup = base;
    }

    groupReportsData(reportsGroup, only);
  };

  const getPermissionToViewReports = (group: string) => {
    switch (group.toLowerCase()) {
      case "warehousing": {
        const hasWarehousingPermission = warehousingPermissions({
          currentUser,
        });

        return hasWarehousingPermission?.view;
      }
      case "operations": {
        const hasOperationsPermission = operationsPermissions({
          subGroup: ResourceSubGroup.OPERATIONS,
          currentUser,
        });

        return hasOperationsPermission?.view;
      }
      case "management": {
        return true;
        const hasManagementPermission = managementPermissions({
          subGroup: ResourceSubGroup.MANAGEMENT,
          currentUser,
        });

        return hasManagementPermission?.view;
      }

      default:
        return true;
    }
  };

  const renderGroups = () => {
    if (groups && groups.length > 0) {
      return groups.map((group) => {
        const hasPermissionToView = getPermissionToViewReports(group.reportGroupName);

        if (!hasPermissionToView) return null;

        if (
          (group.reportGroupName?.toLowerCase() === ResourceName.MANAGEMENT?.toLowerCase() &&
            !isUserTypeAdmin(currentUser)) ||
          (!isMoving(currentUser?.company) &&
            ((group.reportGroupName.toLowerCase() === ResourceName.BUSINESS &&
              !reportPermission.business) ||
              (group.reportGroupName.toLowerCase() === ResourceName.OPERATIONS &&
                !reportPermission.operations) ||
              (group.reportGroupName.toLowerCase() === ResourceName.ORDERS &&
                !reportPermission.orders)))
        )
          return null;

        return (
          <Grid key={group.reportGroupName} container>
            <ReportItem
              addToFavorites={addToFavorites}
              removeFromFavorites={removeFromFavorites}
              searchValue={searchValue}
              group={group}
              checklists={checklists}
              favoriteReports={favoriteReports}
              favoriteChecklists={favoriteChecklists}
              reportsToView={reportsToView}
            />
          </Grid>
        );
      });
    }

    return null;
  };

  const renderContent = () => {
    if (loadingReportGroup || loadingChecklists) {
      return (
        <>
          <Skeleton className={classes.skeleton} />
          <Skeleton className={classes.skeleton} />
          <Skeleton className={classes.skeleton} />
        </>
      );
    }

    if (only === "favorites") {
      return null;
    }

    return renderGroups();
  };

  const renderFavorites = () => {
    if (!loadingReportGroup && !loadingChecklists && only === "favorites") {
      return (
        <FavoriteReports
          reportsGroup={reportsData}
          removeFromFavorites={removeFromFavorites}
          searchValue={searchValue}
          favoriteReports={favoriteReports}
          favoriteChecklists={favoriteChecklists}
        />
      );
    }
    return null;
  };

  // REMOVER QUANDO FOR IMPLEMENTADO O FINANCE REPORT
  if (only === "finance" && isMoving(currentUser?.company)) {
    return <FeatureLocked featureLabel="Finance" />;
  }

  return (
    <div>
      {!isMoving(currentUser?.company) && (
        <TitlePage company={currentUser?.company || ({} as Company)} />
      )}
      <Grid container direction="row" className={classes.reportsContainer}>
        <Grid container className={classes.search}>
          <div className={classes.searchContainer}>
            <SearchFilter handleSearch={(v) => handleSearch(v)} />

            {isShowAction(currentUser, reportPermission.emailSubscription) && (
              <IconButton
                className={classes.goToEditSubscriptionButton}
                onClick={goToEditSubscriptionPage}
              >
                <Sms size={21} color={theme.palette.primary.main} />
              </IconButton>
            )}
          </div>
        </Grid>

        {renderFavorites()}
        {renderContent()}
      </Grid>
      <GenerateReportDrawer ref={generateReportRef} />
    </div>
  );
};

export default Report;
